import { useEffect, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { CallsView } from "./views/CallsView";
import { VoicemailsView } from "./views/VoicemailsView";
import { VoiceSettingsView } from "./views/VoiceSettingsView";
import { VoiceFaqsView } from "./views/VoiceFaqsView";

// Post-cutover (migration 0003, April 22, 2026) `voice.voice_calls.phone_line`
// is populated on every incoming call by the HQ webhook handler, so Line 1 /
// Line 2 / All Lines filter pills are back. We persist the choice in
// localStorage so navigating away and back doesn't reset the filter —
// voicemail-inbox users typically live on one line.
const SUB_TABS = [
  { to: "", label: "Voicemails", match: ["/voice", "/voice/"], adminOnly: false },
  { to: "calls", label: "Calls", match: ["/voice/calls"], adminOnly: false },
  { to: "settings", label: "Settings", match: ["/voice/settings"], adminOnly: false },
  { to: "ai-settings", label: "AI Settings", match: ["/voice/ai-settings"], adminOnly: true },
] as const;

type LineFilter = "all" | "line1" | "line2";

const LINE_STORAGE_KEY = "hq-voice.lineFilter";

const LINE_TABS: { value: LineFilter; label: string }[] = [
  { value: "all", label: "All Lines" },
  { value: "line1", label: "Line 1" },
  { value: "line2", label: "Line 2" },
];

function readInitialLine(): LineFilter {
  if (typeof window === "undefined") return "all";
  const stored = window.localStorage.getItem(LINE_STORAGE_KEY);
  if (stored === "line1" || stored === "line2" || stored === "all") return stored;
  return "all";
}

export function VoiceModule() {
  const { me } = useAuth();
  const isAdmin = !!me?.is_admin;
  const [lineFilter, setLineFilter] = useState<LineFilter>(readInitialLine);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(LINE_STORAGE_KEY, lineFilter);
  }, [lineFilter]);

  const phoneLine: "line1" | "line2" | null =
    lineFilter === "all" ? null : lineFilter;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-shrink-0 items-center justify-between gap-3 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <div className="flex min-w-0 items-center gap-4">
          <h1 className="text-lg font-semibold tracking-tight">
            <span className="text-tribal-red">Tribal Axe</span> Voice
          </h1>
          <nav
            aria-label="Voice views"
            className="hidden items-center gap-0.5 rounded-lg bg-black/30 p-0.5 sm:flex"
          >
            {SUB_TABS.filter((tab) => !tab.adminOnly || isAdmin).map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.to === ""}
                className={({ isActive }) =>
                  `rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-tribal-border text-white"
                      : "text-tribal-muted hover:text-white"
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div
          role="tablist"
          aria-label="Filter by phone line"
          className="hidden items-center gap-0.5 rounded-lg bg-black/30 p-0.5 sm:flex"
        >
          {LINE_TABS.map((tab) => {
            const isActive = lineFilter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setLineFilter(tab.value)}
                className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-tribal-border text-white"
                    : "text-tribal-muted hover:text-white"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      <nav
        aria-label="Voice views (mobile)"
        className="flex flex-shrink-0 overflow-x-auto border-b border-tribal-border bg-tribal-surface sm:hidden"
      >
        {SUB_TABS.filter((tab) => !tab.adminOnly || isAdmin).map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.to === ""}
            className={({ isActive }) =>
              `flex-1 whitespace-nowrap py-2 text-center text-sm font-medium transition-colors ${
                isActive
                  ? "border-b-2 border-tribal-red text-tribal-red"
                  : "text-tribal-muted"
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <div
        role="tablist"
        aria-label="Filter by phone line (mobile)"
        className="flex flex-shrink-0 border-b border-tribal-border bg-tribal-surface sm:hidden"
      >
        {LINE_TABS.map((tab) => {
          const isActive = lineFilter === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setLineFilter(tab.value)}
              className={`flex-1 whitespace-nowrap py-2 text-center text-xs font-medium transition-colors ${
                isActive
                  ? "border-b-2 border-tribal-red text-white"
                  : "text-tribal-muted"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <Routes>
          <Route index element={<VoicemailsView phoneLine={phoneLine} />} />
          <Route path="calls" element={<CallsView phoneLine={phoneLine} />} />
          <Route path="settings" element={<VoiceSettingsView isAdmin={isAdmin} />} />
          <Route path="ai-settings" element={<VoiceFaqsView isAdmin={isAdmin} />} />
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </div>
    </div>
  );
}
